import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { useConsentState, useInvalidateConsentState } from '../hooks/useConsentState';
import { isConsentExemptPath } from '../consentPaths';
import { TermsStage } from './TermsStage';
import { HealthStage } from './HealthStage';
import { DeclineDialog } from './DeclineDialog';
import type { ConsentType } from '../types';

/**
 * Das Einwilligungs-Gate.
 *
 * Haengt in App.tsx auf App-Ebene (neben GoogleAnalytics), nicht in
 * AppLayout. Gruende:
 *   - /chat und /onboarding laufen an AppLayout vorbei, waeren dort also
 *     ungeschuetzt - ausgerechnet die Stellen, an denen Gesundheitsdaten
 *     entstehen.
 *   - AdaptiveLayout rendert fuer eingeloggte Nutzer AppLayout, damit laegen
 *     /legal/privacy und /legal/terms hinter dem Gate. Es verdeckte die
 *     Texte, zu denen es Zustimmung verlangt.
 * Die Ausnahmen stehen deshalb als ausdrueckliche Liste in consentPaths.ts.
 *
 * Zwei Stufen:
 *   1. Nutzungsbedingungen und Datenschutz - blockierend.
 *   2. Gesundheitsdaten (Art. 9) - blockierend, aber mit benannter Folge und
 *      einmaligem Aufschub von sieben Tagen.
 */
export function ConsentGate() {
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const { data: state, isLoading, isError, error } = useConsentState();
  const invalidateConsentState = useInvalidateConsentState();

  const [declineFor, setDeclineFor] = useState<ConsentType[] | null>(null);

  if (authLoading || !user) return null;
  if (isConsentExemptPath(location.pathname)) return null;
  if (isLoading || !state) return null;

  // Bewusst durchlassen, wenn der Stand nicht geladen werden konnte. Ein
  // Netzwerkfehler wuerde die App sonst vollstaendig aussperren - und zwar
  // auch fuer Nutzer, die laengst zugestimmt haben. Beim naechsten
  // erfolgreichen Laden greift das Gate wieder.
  if (isError) {
    console.error('Consent state unavailable, gate stands down:', error);
    return null;
  }

  const handleCompleted = () => {
    setDeclineFor(null);
    invalidateConsentState();
  };

  if (declineFor) {
    return (
      <DeclineDialog
        consentTypes={declineFor}
        state={state}
        source="gate"
        onCancel={() => setDeclineFor(null)}
      />
    );
  }

  // Stufe 1
  const stageOneTypes: ConsentType[] = [];
  if (state.requires_terms) stageOneTypes.push('terms');
  if (state.requires_privacy) stageOneTypes.push('privacy');

  if (stageOneTypes.length > 0) {
    return (
      <TermsStage
        state={state}
        source="gate"
        onCompleted={handleCompleted}
        onDecline={() => setDeclineFor(stageOneTypes)}
      />
    );
  }

  // Stufe 2. health_deferred_until liefert die Datenbank nur, solange die
  // Frist laeuft (get_consent_state filtert gegen NOW()). Der zusaetzliche
  // Vergleich gegen die Uhr des Geraets faengt den Fall ab, dass eine
  // native App tagelang offen bleibt und den Wert aus dem Cache haelt.
  const deferralRunning =
    !!state.health_deferred_until && new Date(state.health_deferred_until) > new Date();

  if (state.requires_health && !deferralRunning) {
    return (
      <HealthStage
        state={state}
        source="gate"
        onCompleted={handleCompleted}
        onDecline={() => setDeclineFor(['health_data'])}
      />
    );
  }

  return null;
}
